/**
 * ARMITA Design Tokens (JS mirror)
 *
 * The canonical design system lives in styles/global.css as CSS custom
 * properties and component classes. This module mirrors those tokens for
 * code that styles via JS objects (canvas overlays, three.js, inline
 * dynamic styles). Keep the two in sync when tuning the palette.
 */

const theme = {
  // Main color palette
  colors: {
    // Primary colors
    primary: {
      main: '#4f9cf0',      // ARMITA blue, tuned for dark surfaces
      light: '#6cb0fa',     // Hover states
      dark: '#3d87d9',      // Active/pressed states
      contrastText: '#FFFFFF'
    },

    // Secondary accent colors
    secondary: {
      main: '#2fc6b7',      // Teal - secondary accent
      light: '#5ad6c9',     // Light teal
      dark: '#1fa093',      // Dark teal
      contrastText: '#FFFFFF'
    },

    // Success states
    success: {
      main: '#34c77b',
      light: '#4cd48d',
      dark: '#279a5f',
      contrastText: '#FFFFFF'
    },

    // Error/warning states
    error: {
      main: '#f0564f',
      light: '#f3726c',
      dark: '#c93f39',
      contrastText: '#FFFFFF'
    },

    warning: {
      main: '#f5b83d',
      light: '#f8c968',
      dark: '#d99c1f',
      contrastText: '#121212'
    },

    // Information states
    info: {
      main: '#4f9cf0',
      light: '#6cb0fa',
      dark: '#3d87d9',
      contrastText: '#FFFFFF'
    },

    // Neutral/grayscale colors for backgrounds and text
    neutral: {
      white: '#FFFFFF',
      black: '#000000',
      background: {
        main: '#0b0d10',      // App canvas
        paper: '#14161a',     // Cards
        elevated: '#1c1f24'   // Elevated surfaces
      },
      border: 'rgba(255, 255, 255, 0.1)',   // Hairline border
      divider: 'rgba(255, 255, 255, 0.06)', // Subtle divider

      // Text colors
      text: {
        primary: 'rgba(255, 255, 255, 0.94)',
        secondary: 'rgba(255, 255, 255, 0.62)',
        disabled: 'rgba(255, 255, 255, 0.26)',
        hint: 'rgba(255, 255, 255, 0.4)'
      }
    },

    // Medical imaging specific colors (functional — used by viewers)
    medical: {
      bone: '#D9C8A3',          // Bone color (muted)
      tissue: '#C25F5F',        // Soft tissue (muted)
      highlight: '#FFEB3B',     // Highlighting important areas
      grid: '#2196F3',          // Grid lines for measurements
      annotation: '#FF4081'     // Annotation markers
    }
  },

  // Typography
  typography: {
    fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Inter', 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    fontSize: {
      xs: '0.75rem',    // 12px
      sm: '0.875rem',   // 14px
      md: '1rem',       // 16px
      lg: '1.125rem',   // 18px
      xl: '1.25rem',    // 20px
      h1: '2rem',       // 32px
      h2: '1.5rem',     // 24px
      h3: '1.25rem',    // 20px
      h4: '1.125rem',   // 18px
      button: '0.875rem' // 14px
    },
    fontWeight: {
      light: 300,
      regular: 400,
      medium: 500,
      semiBold: 600,
      bold: 700
    }
  },

  // Spacing system (in pixels)
  spacing: {
    xs: 4,
    sm: 8,
    md: 16,
    lg: 24,
    xl: 32,
    xxl: 48
  },

  // Border radius
  borderRadius: {
    xs: '5px',
    sm: '8px',
    md: '11px',
    lg: '14px',
    xl: '20px',
    round: '50%'
  },

  // Shadows — layered and soft
  shadows: {
    none: 'none',
    sm: '0 1px 2px rgba(0,0,0,0.28)',
    md: '0 1px 2px rgba(0,0,0,0.24), 0 8px 24px rgba(0,0,0,0.28)',
    lg: '0 2px 4px rgba(0,0,0,0.24), 0 16px 48px rgba(0,0,0,0.4)',
    xl: '0 0 0 1px rgba(255,255,255,0.06), 0 4px 12px rgba(0,0,0,0.35), 0 24px 64px rgba(0,0,0,0.45)',
    inner: 'inset 0 2px 4px rgba(0,0,0,0.3)'
  },

  // Transitions — Apple-style spring-like curve
  transitions: {
    default: 'all 0.24s cubic-bezier(0.32, 0.72, 0, 1)',
    fast: 'all 0.16s cubic-bezier(0.32, 0.72, 0, 1)',
    slow: 'all 0.4s cubic-bezier(0.16, 1, 0.3, 1)'
  },

  // Z-index values
  zIndex: {
    header: 100,
    modal: 1000,
    tooltip: 1500
  },

  // Common component styles
  components: {
    // Button styles
    button: {
      primary: {
        backgroundColor: '#4f9cf0',
        color: '#FFFFFF',
        hoverBackgroundColor: '#6cb0fa',
        activeBackgroundColor: '#3d87d9'
      },
      secondary: {
        backgroundColor: '#2fc6b7',
        color: '#FFFFFF',
        hoverBackgroundColor: '#5ad6c9',
        activeBackgroundColor: '#1fa093'
      },
      success: {
        backgroundColor: '#34c77b',
        color: '#FFFFFF',
        hoverBackgroundColor: '#4cd48d',
        activeBackgroundColor: '#279a5f'
      },
      error: {
        backgroundColor: '#f0564f',
        color: '#FFFFFF',
        hoverBackgroundColor: '#f3726c',
        activeBackgroundColor: '#c93f39'
      },
      disabled: {
        backgroundColor: 'rgba(255, 255, 255, 0.08)',
        color: 'rgba(255, 255, 255, 0.26)',
        cursor: 'not-allowed'
      }
    },

    // Card styles
    card: {
      backgroundColor: '#14161a',
      borderRadius: '14px',
      boxShadow: '0 1px 2px rgba(0,0,0,0.24), 0 8px 24px rgba(0,0,0,0.28)',
      padding: '24px',
      border: '1px solid rgba(255, 255, 255, 0.06)'
    },

    // Input styles
    input: {
      backgroundColor: 'rgba(255, 255, 255, 0.04)',
      borderColor: 'rgba(255, 255, 255, 0.1)',
      borderRadius: '8px',
      focusBorderColor: '#4f9cf0',
      errorBorderColor: '#f0564f',
      height: '40px',
      fontSize: '0.9rem',
      padding: '0 13px',
      color: 'rgba(255, 255, 255, 0.94)'
    },

    // Header/navigation styles
    header: {
      backgroundColor: 'rgba(15, 17, 21, 0.82)',
      color: 'rgba(255, 255, 255, 0.94)',
      height: '60px',
      border: '1px solid rgba(255, 255, 255, 0.06)'
    }
  }
};

export default theme;
